import type { FaceBox } from './faces.js';
import type { ImageCrop, Scene, SceneElement } from './types.js';
import { photoCountLabel } from './layout.js';
import { coverPhotoFrame } from './photo-framing.js';
import { collageAutoCrop, collageFacesCut } from './collage-crop.js';
import {
  collageFitCost,
  collageNoise,
  collageOrientationMismatch,
  completeCollageSlots,
  percentileRanks,
  COLLAGE_NOISE,
  planCollagePlacement,
} from './collage-assign.js';

/**
 * Photo collage — "Kutije sa slikama": many shopper photos on a print side, or
 * spread over several print sheets that are always printed together.
 *
 * One `photo_collage` question takes every photo at once. The storefront
 * places them on the template's slots by itself (collage-assign.ts), frames
 * each one so faces stay whole (collage-crop.ts), and lets the shopper swap,
 * move, re-frame and shuffle — every such edit is a pure command below, so
 * the widget, Studio samples, staff revisions and agent review renders all
 * produce the same answers.
 *
 * The contract (docs/photo-collage.md §2–4):
 *
 *  - Slots are upload image layers named, in order, by the question's
 *    `config.targetElementIds` — the runtime authority. The folder flag
 *    (`SceneGroup.collage`) is an authoring hint the renderer never reads.
 *  - The answer is keyed by ELEMENT ID, never by index: a pool of photos in
 *    pick order plus one fill per slot. Photos in no active slot are spares
 *    ("Rezerva") and never print.
 *  - The renderer never learns about collages: `projectCollageValues` turns an
 *    answer into the per-slot array the multi-upload fan-out already prints,
 *    so each slot prints exactly like a framed single photo (preview = print).
 *  - Nothing here calls a paid service. Faces come from the free local
 *    detector at upload time and travel in the answer.
 *
 * Pure TypeScript: no DOM, no Node, no dependencies.
 */

// ── Constants ────────────────────────────────────────────────────────────────

export const PHOTO_COLLAGE_OPTION_TYPE = 'photo_collage';
export const COLLAGE_ANSWER_VERSION = 1;
export const COLLAGE_MIN_SLOTS = 2;
export const COLLAGE_MAX_SLOTS = 60;
/** Spare photos kept aside by default (`config.maxSpare`, 0…`COLLAGE_MAX_SPARE`). */
export const COLLAGE_DEFAULT_SPARE = 6;
export const COLLAGE_MAX_SPARE = 12;
export const COLLAGE_MAX_FACES = 8;
/** A hand framing may zoom this far into a collage photo. */
export const COLLAGE_MAX_ZOOM = 4;
/** Long side of the draw copy the widget renders collage photos with. */
export const COLLAGE_DRAW_SIDE = 1024;
/** Long side of the tray thumbnail. */
export const COLLAGE_THUMB_SIDE = 256;
/**
 * Effects that bill a provider or run a model per photo. Refused on collage
 * slots by authoring, never run by the widget, stripped by the print worker.
 */
export const COLLAGE_FORBIDDEN_EFFECTS = ['removeBackground', 'faceCutout', 'blurBackground', 'aiStyle', 'enhance'] as const;
export type CollageForbiddenEffect = (typeof COLLAGE_FORBIDDEN_EFFECTS)[number];
/** Option types whose `config.targetElementIds` fan one answer out over several slots. */
export const UPLOAD_TARGET_OPTION_TYPES = ['image_upload', 'vector_upload', PHOTO_COLLAGE_OPTION_TYPE] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_SIDE = 100_000;

// ── Config ───────────────────────────────────────────────────────────────────

export interface PhotoCollageConfig {
  /** Ordered ("Mesto n" = n-th), 2…60 unique; may span several layout folders. */
  targetElementIds: string[];
  /** Default true: empty slots may be filled with repeats ("Ponovi slike"). */
  allowRepeats?: boolean;
  /** Default false: every active slot must hold a photo before ordering. */
  allowEmpty?: boolean;
  /** 0…12, default 6: extra photos kept aside in "Rezerva", never printed. */
  maxSpare?: number;
  /** Default true: automatic framing keeps detected faces whole. */
  faceFraming?: boolean;
  buttonText?: string;
  buttonClass?: string;
  cssClass?: string;
  hidden?: boolean;
  variable?: string;
  guideText?: string;
}

/** A question's collage rules with every default filled in. */
export type CollageRules = Required<Pick<PhotoCollageConfig,
  'targetElementIds' | 'allowRepeats' | 'allowEmpty' | 'maxSpare' | 'faceFraming'>>;

/**
 * Reads a question's collage rules, defaults filled in. Lenient like
 * `choiceDisplayOf`: a stale or odd value reads as the default, so a config
 * never breaks a form. Slot ids are trimmed, blanks and repeats dropped,
 * order kept, at most `COLLAGE_MAX_SLOTS`. Idempotent: rules read as rules.
 */
export function collageRulesOf(config: unknown): CollageRules {
  const c = isRecord(config) ? config : {};
  const ids: string[] = [];
  if (Array.isArray(c.targetElementIds)) {
    for (const raw of c.targetElementIds) {
      if (typeof raw !== 'string') continue;
      const id = raw.trim();
      if (id && !ids.includes(id)) ids.push(id);
      if (ids.length >= COLLAGE_MAX_SLOTS) break;
    }
  }
  const spare = typeof c.maxSpare === 'number' && Number.isFinite(c.maxSpare)
    ? Math.max(0, Math.min(COLLAGE_MAX_SPARE, Math.round(c.maxSpare)))
    : COLLAGE_DEFAULT_SPARE;
  return {
    targetElementIds: ids,
    allowRepeats: c.allowRepeats !== false,
    allowEmpty: c.allowEmpty === true,
    maxSpare: spare,
    faceFraming: c.faceFraming !== false,
  };
}

const BOOLEAN_KEYS = ['allowRepeats', 'allowEmpty', 'faceFraming', 'hidden'] as const;
const STRING_KEYS = ['buttonText', 'buttonClass', 'cssClass', 'variable', 'guideText'] as const;

/**
 * Refuses a collage config the storefront would misread — the pattern of
 * `choiceDisplayError`. Only the keys this contract owns are judged; unset
 * and null are "use the default". Slot rules that need the template (is it an
 * upload image, is it bound) live with the template checks, not here.
 */
export function photoCollageConfigError(config: unknown): string | null {
  const type = PHOTO_COLLAGE_OPTION_TYPE;
  if (!isRecord(config)) return `${type} config must be an object`;
  const ids = config.targetElementIds;
  // Ids exactly as the layers have them: a padded id would read as its trimmed
  // self in the rules but match no binding in the print fan-out.
  if (!Array.isArray(ids) || ids.some((id) => typeof id !== 'string' || id.trim() === '' || id.trim() !== id)) {
    return `${type} config.targetElementIds must be a list of element ids`;
  }
  if (ids.length < COLLAGE_MIN_SLOTS || ids.length > COLLAGE_MAX_SLOTS) {
    return `${type} config.targetElementIds must list ${COLLAGE_MIN_SLOTS}–${COLLAGE_MAX_SLOTS} photo slots`;
  }
  if (new Set(ids.map((id: string) => id.trim())).size !== ids.length) {
    return `${type} config.targetElementIds must not list a slot twice`;
  }
  for (const key of BOOLEAN_KEYS) {
    const value = config[key];
    if (value != null && typeof value !== 'boolean') return `${type} config.${key} must be true or false`;
  }
  const spare = config.maxSpare;
  if (spare != null && (typeof spare !== 'number' || !Number.isInteger(spare) || spare < 0 || spare > COLLAGE_MAX_SPARE)) {
    return `${type} config.maxSpare must be a whole number from 0 to ${COLLAGE_MAX_SPARE}`;
  }
  for (const key of STRING_KEYS) {
    const value = config[key];
    if (value != null && typeof value !== 'string') return `${type} config.${key} must be a string`;
  }
  return null;
}

// ── Answer ───────────────────────────────────────────────────────────────────

/** One picked photo. Size and faces are what placement and framing read. */
export interface CollagePhoto {
  /** An upload's asset id (a UUID). */
  assetId: string;
  /** Upright pixel size of the original. */
  w?: number;
  h?: number;
  /** Fractions of the upright photo; `[]` = looked, none found; absent = not looked. */
  faces?: FaceBox[];
}

/** What one slot holds. */
export interface CollageFill {
  assetId: string;
  /** Always stored when the photo's size is known, so the print gets exactly what was seen. */
  crop?: ImageCrop;
  /** Framed by hand (shopper or staff); a move re-frames it automatically. */
  manual?: true;
}

export interface CollageAnswer {
  v: 1;
  kind: 'collage';
  /** The pool in pick order; photos in no active slot are spares ("Rezerva"). */
  photos: CollagePhoto[];
  /** elementId → fill; absent = empty. */
  slots: Record<string, CollageFill>;
  /** int32, the last arrangement's seed. */
  seed?: number;
}

export type CollageAnswerProblemCode =
  /** Not a v1 collage answer at all; `answer` is null. */
  | 'collage-unreadable'
  /** A photo entry that is not an upload (bad asset id, not an object); dropped. */
  | 'collage-bad-photo'
  /** The same photo listed twice in the pool; the later one dropped. */
  | 'collage-photo-twice'
  /** More photos than slots + spares; the extra (unplaced, latest) ones dropped. `detail` = how many. */
  | 'collage-too-many'
  /** A slot holding a photo that is not in the pool; the fill dropped. */
  | 'collage-bad-fill'
  /** The same photo in two slots of a question that forbids repeats; the later slot emptied. */
  | 'collage-repeat';

export const COLLAGE_ANSWER_PROBLEM_CODES: readonly CollageAnswerProblemCode[] = [
  'collage-unreadable', 'collage-bad-photo', 'collage-photo-twice', 'collage-too-many', 'collage-bad-fill', 'collage-repeat',
];

export interface CollageAnswerProblem {
  code: CollageAnswerProblemCode;
  /** The slot concerned, for fill problems. */
  slot?: string;
  detail?: string;
}

/** Shape check only (`v:1`, `kind:'collage'`, a photo list, a slot map); `normalizeCollageAnswer` judges the rest. */
export function isCollageAnswer(value: unknown): value is CollageAnswer {
  return isRecord(value) && value.v === COLLAGE_ANSWER_VERSION && value.kind === 'collage'
    && Array.isArray(value.photos) && isRecord(value.slots);
}

/** A fresh answer with nothing picked. */
export function emptyCollageAnswer(seed?: number): CollageAnswer {
  return { v: 1, kind: 'collage', photos: [], slots: {}, ...(isSeed(seed) ? { seed } : {}) };
}

/** Empty = no fill in any slot (the question counts as unanswered). */
export function isEmptyCollage(answer: CollageAnswer | null | undefined): boolean {
  return !answer || Object.keys(answer.slots).length === 0;
}

/** A random non-zero int32 seed for a first arrangement or "Promešaj". */
export function randomCollageSeed(random: () => number = Math.random): number {
  return 1 + Math.floor(random() * 0x7ffffffe);
}

/**
 * One picked photo as it survives the wire: a UUID asset id, the size only
 * when both sides are whole numbers 1…100 000, at most 8 faces clamped to the
 * photo (4 dp, score 2 dp). Null when it is not a photo at all.
 */
export function normalizeCollagePhoto(raw: unknown): CollagePhoto | null {
  if (!isRecord(raw) || typeof raw.assetId !== 'string' || !UUID.test(raw.assetId)) return null;
  const w = raw.w;
  const h = raw.h;
  const size = isSide(w) && isSide(h) ? { w, h } : {};
  let faces: FaceBox[] | undefined;
  if (Array.isArray(raw.faces)) {
    faces = [];
    for (const item of raw.faces) {
      const face = faceOf(item);
      if (face) faces.push(face);
      if (faces.length >= COLLAGE_MAX_FACES) break;
    }
  }
  return { assetId: raw.assetId, ...size, ...(faces ? { faces } : {}) };
}

function isSide(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= MAX_SIDE;
}

function faceOf(raw: unknown): FaceBox | null {
  if (!isRecord(raw)) return null;
  const { x, y, w, h, score } = raw;
  if (!finite(x) || !finite(y) || !finite(w) || !finite(h)) return null;
  const x0 = round(clamp(x, 0, 1), 4);
  const y0 = round(clamp(y, 0, 1), 4);
  const bw = round(clamp(x + w, 0, 1) - x0, 4);
  const bh = round(clamp(y + h, 0, 1) - y0, 4);
  if (!(bw > 0 && bh > 0)) return null;
  return { x: x0, y: y0, w: bw, h: bh, ...(finite(score) ? { score: round(clamp(score, 0, 1), 2) } : {}) };
}

/**
 * A slot's framing as stored: `cx`, `cy` in [0, 1] (default the centre),
 * `zoom` in [1, 4], `rotation` in (−180, 180] and dropped when 0, 4 decimals,
 * never `free` or `keep` (those belong to face cut-outs, which a collage slot
 * never has). Undefined when it is not a crop.
 */
export function normalizeCollageCrop(raw: unknown): ImageCrop | undefined {
  if (!isRecord(raw)) return undefined;
  const cx = finite(raw.cx) ? round(clamp(raw.cx, 0, 1), 4) : 0.5;
  const cy = finite(raw.cy) ? round(clamp(raw.cy, 0, 1), 4) : 0.5;
  const zoom = finite(raw.zoom) ? round(clamp(raw.zoom, 1, COLLAGE_MAX_ZOOM), 4) : 1;
  // Rounded before the wrap (so 720.00001 is 0 and −179.99999 is 180) and
  // again after it, since the wrap's float sums leave noise (10.1234 must
  // stay 10.1234, not 10.123400000000004).
  let rotation = finite(raw.rotation) ? round(raw.rotation, 4) : 0;
  rotation = ((rotation % 360) + 360) % 360;
  rotation = round(rotation > 180 ? rotation - 360 : rotation, 4);
  if (rotation <= -180) rotation = 180;
  return { cx, cy, zoom, ...(rotation !== 0 ? { rotation } : {}) };
}

/**
 * Parses whatever arrived (widget, server, order sheet, staff revision) into
 * a clean answer — one function for all of them, idempotent:
 * `normalize(normalize(x).answer)` equals `normalize(x).answer` with no problems.
 *
 * - not `{v:1, kind:'collage'}` → `answer: null`, `collage-unreadable`;
 * - photos: UUID asset ids, unique, at most slots + `maxSpare` (placed photos
 *   are kept first), size and faces cleaned;
 * - slots: keys not in the question's slots are dropped silently (a draft
 *   saved against an older release); a fill must name a pool photo; the same
 *   photo in two slots only when repeats are allowed; crops cleaned; `manual`
 *   kept only when `true`;
 * - unknown keys dropped. `config` is the question config or its rules.
 */
export function normalizeCollageAnswer(
  raw: unknown,
  config: unknown,
): { answer: CollageAnswer | null; problems: CollageAnswerProblem[] } {
  if (!isRecord(raw) || raw.v !== COLLAGE_ANSWER_VERSION || raw.kind !== 'collage') {
    return { answer: null, problems: [{ code: 'collage-unreadable' }] };
  }
  const rules = collageRulesOf(config);
  const problems: CollageAnswerProblem[] = [];
  const targets = rules.targetElementIds;
  const rawSlots = isRecord(raw.slots) ? raw.slots : {};

  const cleaned: CollagePhoto[] = [];
  const seen = new Set<string>();
  const rawPhotos = Array.isArray(raw.photos) ? raw.photos : [];
  rawPhotos.forEach((item, i) => {
    const photo = normalizeCollagePhoto(item);
    if (!photo) {
      problems.push({ code: 'collage-bad-photo', detail: String(i) });
      return;
    }
    if (seen.has(photo.assetId)) {
      problems.push({ code: 'collage-photo-twice', detail: photo.assetId });
      return;
    }
    seen.add(photo.assetId);
    cleaned.push(photo);
  });

  // Over the cap: placed photos first, then the earliest picked.
  const cap = targets.length + rules.maxSpare;
  let photos = cleaned;
  if (cleaned.length > cap) {
    const placed = new Set<string>();
    for (const id of targets) {
      const fill = rawSlots[id];
      if (isRecord(fill) && typeof fill.assetId === 'string') placed.add(fill.assetId);
    }
    const keep = new Set<string>();
    for (const p of cleaned) if (placed.has(p.assetId) && keep.size < cap) keep.add(p.assetId);
    for (const p of cleaned) if (!keep.has(p.assetId) && keep.size < cap) keep.add(p.assetId);
    photos = cleaned.filter((p) => keep.has(p.assetId));
    problems.push({ code: 'collage-too-many', detail: String(cleaned.length - photos.length) });
  }
  const pool = new Set(photos.map((p) => p.assetId));

  const slots: Record<string, CollageFill> = {};
  const used = new Set<string>();
  for (const id of targets) {
    if (!Object.prototype.hasOwnProperty.call(rawSlots, id)) continue;
    const fill = rawSlots[id];
    if (fill == null) continue;
    if (!isRecord(fill) || typeof fill.assetId !== 'string' || !pool.has(fill.assetId)) {
      problems.push({ code: 'collage-bad-fill', slot: id });
      continue;
    }
    if (!rules.allowRepeats && used.has(fill.assetId)) {
      problems.push({ code: 'collage-repeat', slot: id, detail: fill.assetId });
      continue;
    }
    used.add(fill.assetId);
    const crop = normalizeCollageCrop(fill.crop);
    slots[id] = { assetId: fill.assetId, ...(crop ? { crop } : {}), ...(fill.manual === true ? { manual: true as const } : {}) };
  }
  const seed = isSeed(raw.seed) ? raw.seed : undefined;
  return {
    answer: { v: 1, kind: 'collage', photos, slots, ...(seed !== undefined ? { seed } : {}) },
    problems,
  };
}

// ── Projection (the mirrored step: api resolve.ts and widget preview.ts) ─────

/** What slot `elementId` prints: `{assetId, crop}` exactly as a framed single photo, or null. */
export function collageSlotValue(answer: CollageAnswer, elementId: string): { assetId: string; crop?: ImageCrop } | null {
  const fill = Object.prototype.hasOwnProperty.call(answer.slots, elementId) ? answer.slots[elementId] : undefined;
  if (!fill || typeof fill.assetId !== 'string') return null;
  return fill.crop ? { assetId: fill.assetId, crop: fill.crop } : { assetId: fill.assetId };
}

/**
 * Replaces each collage answer in `values` by the per-slot array the
 * multi-upload fan-out prints: `array[i]` is the fill of the slot seated at
 * `i` (`seats` = option id → element id → seat, exactly as `uploadFanOut`
 * builds them). A collage wired to fewer than 2 slots projects to null —
 * printing nothing beats printing a photo in the wrong slot. Returns the
 * same object when there is no collage answer, so other products pay nothing.
 */
export function projectCollageValues<T extends Record<string, unknown>>(
  values: T,
  seats: ReadonlyMap<string, ReadonlyMap<string, number>>,
): T {
  let out: Record<string, unknown> | undefined;
  for (const key of Object.keys(values)) {
    const value = values[key];
    if (!isCollageAnswer(value)) continue;
    out ??= { ...values };
    const seat = seats.get(key);
    if (!seat || seat.size < COLLAGE_MIN_SLOTS) {
      out[key] = null;
      continue;
    }
    const array: Array<{ assetId: string; crop?: ImageCrop } | null> = new Array(seat.size).fill(null);
    for (const [elementId, index] of seat) {
      if (Number.isInteger(index) && index >= 0 && index < array.length) array[index] = collageSlotValue(value, elementId);
    }
    out[key] = array;
  }
  return (out ?? values) as T;
}

/**
 * Every question's numbered slot list — the multi-upload fields' and the
 * collages' — keyed by option id, ready for `uploadFanOut` / `applyValues`.
 * The shared copy of the widget's and the commit path's lists. A collage's
 * list is read by `collageRulesOf` (trimmed, unique, at most 60), so the
 * print fan-out seats exactly the slots the commands and `collageMissing` see.
 */
export function uploadTargetsOf(
  options: ReadonlyArray<{ id: string; type: string; config?: Record<string, unknown> | null }>,
): Record<string, string[]> {
  const targets: Record<string, string[]> = {};
  for (const option of options) {
    if (!(UPLOAD_TARGET_OPTION_TYPES as readonly string[]).includes(option.type)) continue;
    const declared = option.config?.targetElementIds;
    if (!Array.isArray(declared)) continue;
    const ids = option.type === PHOTO_COLLAGE_OPTION_TYPE
      ? collageRulesOf(option.config).targetElementIds
      : declared.filter((id): id is string => typeof id === 'string' && id !== '');
    if (ids.length > 0) targets[option.id] = ids;
  }
  return targets;
}

/**
 * The answer's asset ids, unique, pool order: every photo (spares too — the
 * ownership check looks at all of them), or only the placed ones.
 */
export function collageAssetIds(answer: CollageAnswer, options: { placedOnly?: boolean } = {}): string[] {
  const out: string[] = [];
  const add = (id: unknown) => {
    if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
  };
  const placed = new Set(Object.values(answer.slots).map((f) => f?.assetId));
  for (const photo of answer.photos) if (!options.placedOnly || placed.has(photo.assetId)) add(photo.assetId);
  for (const fill of Object.values(answer.slots)) add(fill?.assetId);
  return out;
}

/**
 * Empty active slots — judged AFTER resolve, because only then is it known
 * which layout variant is visible. 0 when the question allows empty slots.
 * Commit refuses an order with a count above 0 (422 `collage-incomplete`).
 */
export function collageMissing(
  answer: CollageAnswer | null | undefined,
  activeTargetIds: Iterable<string>,
  rules: CollageRules,
): number {
  if (rules.allowEmpty) return 0;
  const targets = new Set(rules.targetElementIds);
  let missing = 0;
  for (const id of new Set(activeTargetIds)) {
    if (targets.size > 0 && !targets.has(id)) continue;
    if (!answer || !collageSlotValue(answer, id)) missing++;
  }
  return missing;
}

/** Photos in no active slot (or in no slot at all, without `activeTargetIds`): "Rezerva". */
export function collageSpares(answer: CollageAnswer, activeTargetIds?: Iterable<string>): string[] {
  const active = activeTargetIds ? new Set(activeTargetIds) : undefined;
  const placed = new Set<string>();
  for (const [id, fill] of Object.entries(answer.slots)) if (fill && (!active || active.has(id))) placed.add(fill.assetId);
  return answer.photos.map((p) => p.assetId).filter((id, i, all) => !placed.has(id) && all.indexOf(id) === i);
}

/**
 * The shopper-facing summary: "24 slike" — filled slots, counted over the
 * active slots when they are known (a product with several layouts keeps the
 * fills of the hidden one in the answer).
 */
export function describeCollage(answer: CollageAnswer, activeTargetIds?: Iterable<string>): string {
  const active = activeTargetIds ? new Set(activeTargetIds) : undefined;
  const filled = Object.entries(answer.slots).filter(([id, fill]) => fill && (!active || active.has(id))).length;
  return photoCountLabel(filled);
}

// ── Slots ────────────────────────────────────────────────────────────────────

/** One active slot as placement and framing see it. */
export interface CollageSlotGeom {
  elementId: string;
  /** Position in the question's `targetElementIds` ("Mesto index+1"). */
  index: number;
  /** The element's own size in scene px — never its rotated box. */
  w: number;
  h: number;
  area: number;
  /** 0 = smallest … 1 = biggest of the active slots; ties share a rank. */
  sizeRank: number;
  /** Centre in scene px (repeats are kept apart by it). */
  cx?: number;
  cy?: number;
  /** The print side's dpi: what the slot's print resolution is judged against. */
  dpi: number;
  /**
   * The print side the slot is on, when a collage spans several sheets
   * (`collageSlotGeomsOn`). Absent on a one-sheet collage.
   */
  sideId?: string;
  /**
   * The slot's outline when it is not a plain rectangle: its clip shape
   * ("heart") or `mask:<assetId>`. A hand framing moves with its photo only
   * between slots of the same proportions and the same outline.
   */
  shape?: string;
}

/** A slot's outline for {@link CollageSlotGeom.shape}; undefined for a plain rectangle. */
export function collageSlotShape(el: SceneElement): string | undefined {
  if (el.type !== 'image') return undefined;
  if (el.mask?.assetId) return `mask:${el.mask.assetId}`;
  return el.clipShape && el.clipShape !== 'rect' ? el.clipShape : undefined;
}

/** A slot as commands and placement accept it; missing fields are derived. */
export type CollageSlotInput = Pick<CollageSlotGeom, 'elementId' | 'w' | 'h'> & Partial<CollageSlotGeom>;

/**
 * The active slots of a collage on a (resolved) print scene: targets that are
 * image elements drawn on it — visible, not preview-only — in target order.
 * Pass the scene after resolve so the chosen layout variant decides.
 */
export function collageSlotGeoms(
  printScene: Pick<Scene, 'elements'> & Partial<Pick<Scene, 'dpi'>>,
  targets: readonly string[],
): CollageSlotGeom[] {
  const byId = new Map(printScene.elements.map((el) => [el.id, el]));
  const found: CollageSlotInput[] = [];
  const seen = new Set<string>();
  targets.forEach((id, index) => {
    const el = byId.get(id);
    if (!el || seen.has(id) || el.type !== 'image' || el.visible === false || el.previewOnly) return;
    const w = Math.abs(el.transform.w);
    const h = Math.abs(el.transform.h);
    if (!(w > 0 && h > 0)) return;
    seen.add(id);
    const shape = collageSlotShape(el);
    found.push({ elementId: id, index, w, h, cx: el.transform.cx, cy: el.transform.cy, dpi: printScene.dpi || 300, ...(shape ? { shape } : {}) });
  });
  return completeCollageSlots(found);
}

/**
 * The active slots of a collage spread over several print sides (a box
 * printed on two sheets), each tagged with its side, in target order. Sizes
 * are compared on paper: a slot's rank is by its printed area, so sheets of
 * different resolution rank fairly. With one side it is
 * {@link collageSlotGeoms} plus the side id.
 */
export function collageSlotGeomsOn(
  printScenes: ReadonlyArray<Pick<Scene, 'id' | 'elements'> & Partial<Pick<Scene, 'dpi'>>>,
  targets: readonly string[],
): CollageSlotGeom[] {
  const at = new Map<string, { el: SceneElement; scene: (typeof printScenes)[number] }>();
  for (const scene of printScenes) for (const el of scene.elements) if (!at.has(el.id)) at.set(el.id, { el, scene });
  const found: Array<CollageSlotInput & { sideId: string }> = [];
  const seen = new Set<string>();
  targets.forEach((id, index) => {
    const hit = at.get(id);
    if (!hit || seen.has(id)) return;
    const { el, scene } = hit;
    if (el.type !== 'image' || el.visible === false || el.previewOnly) return;
    const w = Math.abs(el.transform.w);
    const h = Math.abs(el.transform.h);
    if (!(w > 0 && h > 0)) return;
    seen.add(id);
    const shape = collageSlotShape(el);
    found.push({ elementId: id, index, w, h, cx: el.transform.cx, cy: el.transform.cy, dpi: scene.dpi || 300, sideId: scene.id, ...(shape ? { shape } : {}) });
  });
  const ranks = percentileRanks(found.map((s) => (s.w * s.h) / (s.dpi! * s.dpi!)));
  return completeCollageSlots(found.map((s, i) => ({ ...s, sizeRank: ranks[i]! })));
}

/**
 * The element with every paid per-photo effect removed — the print worker's
 * last guard on collage-filled slots. The same object when there is nothing
 * to strip.
 */
export function withoutCollageEffects<T extends SceneElement>(element: T): T {
  const el = element as SceneElement;
  if (el.type !== 'image' || !el.effects?.length) return element;
  const forbidden = new Set<string>(COLLAGE_FORBIDDEN_EFFECTS);
  const kept = el.effects.filter((effect) => !forbidden.has(effect.kind));
  if (kept.length === el.effects.length) return element;
  if (kept.length > 0) return { ...el, effects: kept } as T;
  const { effects: _dropped, ...rest } = el;
  void _dropped;
  return rest as T;
}

// ── Commands (pure, atomic: a new answer, or the same one when nothing applies) ─

/** What every command needs: the active slots and the question's rules. */
export interface CollageContext {
  /** The ACTIVE slots (targets visible after resolve), e.g. from `collageSlotGeoms`. */
  slots: readonly CollageSlotInput[];
  rules: CollageRules;
}

/** A thing the shopper can tap on the board: a slot (filled or empty) or a spare photo. */
export type CollageRef = { slot: string } | { spare: string };

interface Ctx {
  slots: CollageSlotGeom[];
  byId: Map<string, CollageSlotGeom>;
  rules: CollageRules;
  targets: string[];
}

function contextOf(ctx: CollageContext): Ctx {
  const rules = collageRulesOf(ctx.rules);
  const allowed = new Set(rules.targetElementIds);
  const slots = completeCollageSlots(ctx.slots.filter((s) => allowed.size === 0 || allowed.has(s.elementId)));
  return { slots, byId: new Map(slots.map((s) => [s.elementId, s])), rules, targets: rules.targetElementIds };
}

function photoOf(answer: CollageAnswer, assetId: string): CollagePhoto | undefined {
  return answer.photos.find((p) => p.assetId === assetId);
}

function autoFill(photo: CollagePhoto | undefined, assetId: string, slot: CollageSlotGeom | undefined, c: Ctx): CollageFill {
  const crop = photo && slot ? collageAutoCrop(photo, slot, { faceFraming: c.rules.faceFraming }).crop : undefined;
  return crop ? { assetId, crop } : { assetId };
}

/** Rebuilds an answer with its slots in target order (stable JSON, stable hashes). */
function build(answer: CollageAnswer, photos: CollagePhoto[], slots: Record<string, CollageFill>, c: Ctx, seed?: number): CollageAnswer {
  const ordered: Record<string, CollageFill> = {};
  for (const id of c.targets) if (slots[id]) ordered[id] = slots[id];
  for (const [id, fill] of Object.entries(slots)) if (!(id in ordered) && fill) ordered[id] = fill;
  const s = seed !== undefined ? seed : answer.seed;
  return { v: 1, kind: 'collage', photos, slots: ordered, ...(isSeed(s) ? { seed: s } : {}) };
}

/** Puts `assetId` into active slot `elementId` with its automatic framing; its fills in inactive slots move with it. */
function place(answer: CollageAnswer, slots: Record<string, CollageFill>, elementId: string, assetId: string, c: Ctx): void {
  slots[elementId] = autoFill(photoOf(answer, assetId), assetId, c.byId.get(elementId), c);
  for (const [id, fill] of Object.entries(slots)) {
    if (id !== elementId && fill?.assetId === assetId && !c.byId.has(id)) delete slots[id];
  }
}

function activeUses(slots: Record<string, CollageFill>, c: Ctx): Map<string, number> {
  const uses = new Map<string, number>();
  for (const s of c.slots) {
    const fill = slots[s.elementId];
    if (fill) uses.set(fill.assetId, (uses.get(fill.assetId) ?? 0) + 1);
  }
  return uses;
}

function usedAnywhere(slots: Record<string, CollageFill>, assetId: string, except?: string): boolean {
  return Object.entries(slots).some(([id, fill]) => id !== except && fill?.assetId === assetId);
}

export interface CollageAddResult {
  answer: CollageAnswer;
  /** New photos that went straight onto a slot. */
  placed: string[];
  /** New photos waiting in "Rezerva". */
  spares: string[];
  /** Photos that did not fit (over the cap) or are not uploads; the answer does not hold them. */
  refused: string[];
}

/**
 * Adds freshly picked photos. They go into EMPTY active slots only — placed
 * photos never move — chosen by the placement cost; the rest wait as spares
 * while the pool has room (`active slots + maxSpare`), and anything beyond is
 * refused. A photo already in the pool is skipped. `seed` (default the
 * answer's) decides between equally good slots and is stored when given.
 */
export function addCollagePhotos(
  answer: CollageAnswer | null | undefined,
  incoming: readonly unknown[],
  ctx: CollageContext,
  options: { seed?: number } = {},
): CollageAddResult {
  const c = contextOf(ctx);
  const base = answer ?? emptyCollageAnswer();
  const inPool = new Set(base.photos.map((p) => p.assetId));
  const room = Math.max(0, Math.min(
    c.slots.length + c.rules.maxSpare - base.photos.length,
    c.targets.length + c.rules.maxSpare - base.photos.length,
  ));
  const accepted: CollagePhoto[] = [];
  const refused: string[] = [];
  for (const raw of incoming) {
    const photo = normalizeCollagePhoto(raw);
    if (!photo) {
      refused.push(isRecord(raw) && typeof raw.assetId === 'string' ? raw.assetId : '');
      continue;
    }
    if (inPool.has(photo.assetId)) continue;
    inPool.add(photo.assetId);
    if (accepted.length >= room) refused.push(photo.assetId);
    else accepted.push(photo);
  }
  if (accepted.length === 0) return { answer: base, placed: [], spares: [], refused };
  const seed = isSeed(options.seed) ? options.seed : base.seed ?? 0;
  const photos = [...base.photos, ...accepted];
  const next = { ...base, photos };
  const empty = c.slots.filter((s) => !base.slots[s.elementId]);
  const plan = planCollagePlacement(accepted, empty, { seed, faceFraming: c.rules.faceFraming });
  const slots = { ...base.slots };
  const placed: string[] = [];
  for (const s of empty) {
    const assetId = plan.bySlot[s.elementId];
    if (!assetId) continue;
    place(next, slots, s.elementId, assetId, c);
    placed.push(assetId);
  }
  return {
    answer: build(base, photos, slots, c, isSeed(options.seed) ? options.seed : undefined),
    placed,
    spares: plan.spares,
    refused,
  };
}

type Resolved = { kind: 'slot'; id: string; fill?: CollageFill } | { kind: 'spare'; assetId: string };

function resolveRef(answer: CollageAnswer, ref: CollageRef, c: Ctx): Resolved | null {
  if (!isRecord(ref)) return null;
  if ('slot' in ref && typeof ref.slot === 'string') {
    if (!c.byId.has(ref.slot)) return null;
    const fill = answer.slots[ref.slot];
    return fill ? { kind: 'slot', id: ref.slot, fill } : { kind: 'slot', id: ref.slot };
  }
  if ('spare' in ref && typeof ref.spare === 'string') {
    const spares = collageSpares(answer, c.slots.map((s) => s.elementId));
    return spares.includes(ref.spare) ? { kind: 'spare', assetId: ref.spare } : null;
  }
  return null;
}

/** Proportions within 1% and the same outline: a framing looks the same in both. */
export function sameCollageFrame(a: Pick<CollageSlotGeom, 'w' | 'h' | 'shape'>, b: Pick<CollageSlotGeom, 'w' | 'h' | 'shape'>): boolean {
  if (!(a.w > 0 && a.h > 0 && b.w > 0 && b.h > 0)) return false;
  const ra = a.w / a.h;
  const rb = b.w / b.h;
  return Math.abs(ra - rb) <= 0.01 * Math.max(ra, rb) && (a.shape ?? '') === (b.shape ?? '');
}

/**
 * A photo moved from slot `from` into slot `to` by an exchange. An automatic
 * framing is made again for the new slot. A hand framing moves with the photo:
 * whole between slots of the same proportions and outline, otherwise as the
 * point the shopper centred, its zoom and turn, clamped to cover the new slot.
 */
function carry(answer: CollageAnswer, slots: Record<string, CollageFill>, to: string, fill: CollageFill, from: string, c: Ctx): void {
  const target = c.byId.get(to);
  const source = c.byId.get(from);
  if (!fill.manual || !fill.crop || !target || !source) {
    place(answer, slots, to, fill.assetId, c);
    return;
  }
  const photo = photoOf(answer, fill.assetId);
  let crop: ImageCrop = fill.crop;
  if (!sameCollageFrame(source, target) && photo?.w && photo.h) {
    crop = normalizeCollageCrop(coverPhotoFrame({ w: photo.w, h: photo.h }, target, crop).crop) ?? crop;
  }
  const auto = photo ? collageAutoCrop(photo, target, { faceFraming: c.rules.faceFraming }).crop : undefined;
  slots[to] = auto && sameCrop(crop, auto, 0.001) ? { assetId: fill.assetId, crop: auto } : { assetId: fill.assetId, crop, manual: true };
  for (const [id, other] of Object.entries(slots)) {
    if (id !== to && other?.assetId === fill.assetId && !c.byId.has(id)) delete slots[id];
  }
}

/**
 * Exchange A and B (the board's "Zameni mesto", or a photo dragged onto
 * another). Slot ↔ slot swaps the two photos; slot ↔ empty slot moves one;
 * slot ↔ spare puts the spare in and sends the displaced photo to "Rezerva"
 * (a repeat simply stays in its other slot). An automatic framing is made
 * again for the photo's new slot; a hand framing moves with its photo
 * (`carry`). A spare coming in is framed automatically. Empty ↔ empty,
 * spare ↔ spare and a ref tapped twice change nothing.
 */
export function exchangeCollage(answer: CollageAnswer, a: CollageRef, b: CollageRef, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const x = resolveRef(answer, a, c);
  const y = resolveRef(answer, b, c);
  if (!x || !y) return answer;
  if (x.kind === 'spare' && y.kind === 'spare') return answer;
  if (x.kind === 'slot' && y.kind === 'slot') {
    if (x.id === y.id || (!x.fill && !y.fill)) return answer;
    if (x.fill && y.fill && x.fill.assetId === y.fill.assetId) return answer;
    const slots = { ...answer.slots };
    delete slots[x.id];
    delete slots[y.id];
    if (y.fill) carry(answer, slots, x.id, y.fill, y.id, c);
    if (x.fill) carry(answer, slots, y.id, x.fill, x.id, c);
    return build(answer, answer.photos, slots, c);
  }
  const slot = (x.kind === 'slot' ? x : y) as Extract<Resolved, { kind: 'slot' }>;
  const spare = (x.kind === 'spare' ? x : y) as Extract<Resolved, { kind: 'spare' }>;
  const slots = { ...answer.slots };
  place(answer, slots, slot.id, spare.assetId, c);
  return build(answer, answer.photos, slots, c);
}

/**
 * Empties a slot ("Ukloni"). The photo leaves the answer unless another slot
 * still holds it.
 */
export function removeFromSlot(answer: CollageAnswer, elementId: string, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const fill = answer.slots[elementId];
  if (!c.byId.has(elementId) || !fill) return answer;
  const slots = { ...answer.slots };
  delete slots[elementId];
  const photos = usedAnywhere(slots, fill.assetId) ? answer.photos : answer.photos.filter((p) => p.assetId !== fill.assetId);
  return build(answer, photos, slots, c);
}

/** Takes a photo out of the answer altogether — a spare dropped from "Rezerva", or a photo wherever it sits. */
export function removeCollagePhoto(answer: CollageAnswer, assetId: string): CollageAnswer {
  const inPool = answer.photos.some((p) => p.assetId === assetId);
  const inSlots = Object.values(answer.slots).some((f) => f?.assetId === assetId);
  if (!inPool && !inSlots) return answer;
  const slots: Record<string, CollageFill> = {};
  for (const [id, fill] of Object.entries(answer.slots)) if (fill && fill.assetId !== assetId) slots[id] = fill;
  return {
    v: 1,
    kind: 'collage',
    photos: answer.photos.filter((p) => p.assetId !== assetId),
    slots,
    ...(isSeed(answer.seed) ? { seed: answer.seed } : {}),
  };
}

/**
 * A new photo straight into one slot ("Nova slika", "Dodaj sliku ovde"). The
 * slot's old photo leaves the answer unless another slot still holds it. A
 * photo already in the pool is simply placed (refused as a repeat when the
 * question forbids repeats and another active slot holds it).
 */
export function replaceInSlot(answer: CollageAnswer, elementId: string, photo: unknown, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const clean = normalizeCollagePhoto(photo);
  if (!clean || !c.byId.has(elementId)) return answer;
  const old = answer.slots[elementId]?.assetId;
  if (old === clean.assetId) return answer;
  const existing = photoOf(answer, clean.assetId);
  if (existing && !c.rules.allowRepeats && (activeUses(answer.slots, c).get(clean.assetId) ?? 0) > 0) return answer;
  const slots = { ...answer.slots };
  delete slots[elementId];
  let photos = answer.photos;
  if (old && !usedAnywhere(slots, old)) photos = photos.filter((p) => p.assetId !== old);
  if (!existing) {
    if (photos.length + 1 > c.targets.length + c.rules.maxSpare) return answer;
    photos = [...photos, clean];
  }
  const next = { ...answer, photos };
  place(next, slots, elementId, clean.assetId, c);
  return build(answer, photos, slots, c);
}

/**
 * A hand framing from the framer ("Kadriraj"): cleaned, clamped the way the
 * renderer draws, and marked `manual` — unless it is the automatic framing
 * after all (within 0.001), which stays automatic.
 */
export function setSlotCrop(answer: CollageAnswer, elementId: string, crop: unknown, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const fill = answer.slots[elementId];
  const slot = c.byId.get(elementId);
  let clean = normalizeCollageCrop(crop);
  if (!fill || !slot || !clean) return answer;
  const photo = photoOf(answer, fill.assetId);
  if (photo?.w && photo.h) clean = normalizeCollageCrop(coverPhotoFrame({ w: photo.w, h: photo.h }, slot, clean).crop) ?? clean;
  const auto = photo ? collageAutoCrop(photo, slot, { faceFraming: c.rules.faceFraming }).crop : undefined;
  const next: CollageFill = auto && sameCrop(clean, auto, 0.001)
    ? { assetId: fill.assetId, crop: auto }
    : { assetId: fill.assetId, crop: clean, manual: true };
  if (sameFill(fill, next)) return answer;
  return build(answer, answer.photos, { ...answer.slots, [elementId]: next }, c);
}

/** "Automatski": the slot's automatic framing back, `manual` cleared. */
export function resetSlotCrop(answer: CollageAnswer, elementId: string, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const fill = answer.slots[elementId];
  const slot = c.byId.get(elementId);
  if (!fill || !slot) return answer;
  const next = autoFill(photoOf(answer, fill.assetId), fill.assetId, slot, c);
  if (sameFill(fill, next)) return answer;
  return build(answer, answer.photos, { ...answer.slots, [elementId]: next }, c);
}

/**
 * "Promešaj": a new arrangement, with `seed`, of the photos that are on the
 * active slots. Which photos print does not change: spares stay in "Rezerva"
 * (the shopper parked them there), and only a swap brings one in. A photo that
 * stays in its slot keeps its framing; every moved photo is re-framed
 * automatically. If the answer filled gaps with repeats, the gaps are filled
 * again. The seed is stored.
 */
export function shuffleCollage(answer: CollageAnswer, seed: number, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const uses = activeUses(answer.slots, c);
  const filled = [...uses.values()].reduce((sum, n) => sum + n, 0);
  const hadRepeats = filled > uses.size;
  const onBoard = answer.photos.filter((p) => uses.has(p.assetId));
  const plan = planCollagePlacement(onBoard, c.slots, { seed, faceFraming: c.rules.faceFraming });
  const slots = { ...answer.slots };
  for (const s of c.slots) delete slots[s.elementId];
  for (const s of c.slots) {
    const assetId = plan.bySlot[s.elementId];
    if (!assetId) continue;
    const before = answer.slots[s.elementId];
    if (before?.assetId === assetId) slots[s.elementId] = before;
    else place(answer, slots, s.elementId, assetId, c);
  }
  const shuffled = build(answer, answer.photos, slots, c, isSeed(seed) ? seed : undefined);
  return hadRepeats && c.rules.allowRepeats ? fillRepeats(shuffled, seed, ctx) : shuffled;
}

/**
 * "Ponovi slike": every empty active slot gets a photo again — never next to
 * its twin when that can be avoided, then the least-used photo, then one that
 * faces the slot's way (the seed's noise never turns a photo), then the best
 * fit (plus the seed's noise). Nothing happens when the question forbids
 * repeats or there is no photo.
 */
export function fillRepeats(answer: CollageAnswer, seed: number, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  if (!c.rules.allowRepeats || answer.photos.length === 0) return answer;
  const empties = c.slots.filter((s) => !answer.slots[s.elementId]);
  if (empties.length === 0) return answer;
  const slots = { ...answer.slots };
  const uses = activeUses(slots, c);
  const s0 = isSeed(seed) ? seed : 0;
  for (const slot of empties) {
    let best: { assetId: string; key: [number, number, number, number] } | undefined;
    for (const photo of answer.photos) {
      const near = c.slots.some((t) => t.elementId !== slot.elementId && slots[t.elementId]?.assetId === photo.assetId && nextTo(slot, t)) ? 1 : 0;
      const turned = collageOrientationMismatch(photo, slot) ? 1 : 0;
      const fit = collageFitCost(photo, slot, { faceFraming: c.rules.faceFraming }) + COLLAGE_NOISE * collageNoise(s0, photo.assetId, slot.elementId);
      const key: [number, number, number, number] = [near, uses.get(photo.assetId) ?? 0, turned, fit];
      if (!best || lexLess(key, best.key)) best = { assetId: photo.assetId, key };
    }
    if (!best) continue;
    place(answer, slots, slot.elementId, best.assetId, c);
    uses.set(best.assetId, (uses.get(best.assetId) ?? 0) + 1);
  }
  return build(answer, answer.photos, slots, c);
}

/**
 * After a layout switch ("15 slika" ↔ "24 slike"): the photos that are in no
 * active slot — spares and the photos of the slots that just became hidden —
 * go into the empty active slots by the placement cost. Fills of hidden slots
 * stay in the answer (switching back finds them) unless their photo moves.
 */
export function relayoutCollage(answer: CollageAnswer, ctx: CollageContext): CollageAnswer {
  const c = contextOf(ctx);
  const empties = c.slots.filter((s) => !answer.slots[s.elementId]);
  const waiting = collageSpares(answer, c.slots.map((s) => s.elementId));
  if (empties.length === 0 || waiting.length === 0) return answer;
  const candidates = waiting.map((id) => photoOf(answer, id)).filter((p): p is CollagePhoto => !!p);
  const plan = planCollagePlacement(candidates, empties, { seed: answer.seed ?? 0, faceFraming: c.rules.faceFraming });
  const slots = { ...answer.slots };
  let changed = false;
  for (const s of empties) {
    const assetId = plan.bySlot[s.elementId];
    if (!assetId) continue;
    place(answer, slots, s.elementId, assetId, c);
    changed = true;
  }
  return changed ? build(answer, answer.photos, slots, c) : answer;
}

export interface CollageStatus {
  /** Active slots. */
  active: number;
  filled: number;
  /** Empty active slots. */
  missing: number;
  /** Different photos on the active slots. */
  distinct: number;
  /** Photos on no active slot ("Rezerva"); never printed. */
  spares: number;
  /** Active slots holding a photo that another active slot also holds. */
  repeats: number;
  /** Active slots whose automatic framing may cut a face (hand framings are the shopper's call). */
  facesCut: string[];
  /** Ready to order: every active slot filled, or something filled when empty slots are allowed. */
  complete: boolean;
}

/** Where the question stands, for the step's messages and the board's badges. */
export function collageStatus(
  answer: CollageAnswer | null | undefined,
  activeSlots: readonly CollageSlotInput[],
  rules: CollageRules,
): CollageStatus {
  const r = collageRulesOf(rules);
  const slots = completeCollageSlots(activeSlots);
  const fills = answer?.slots ?? {};
  let filled = 0;
  const distinct = new Set<string>();
  const facesCut: string[] = [];
  for (const slot of slots) {
    const fill = fills[slot.elementId];
    if (!fill) continue;
    filled++;
    distinct.add(fill.assetId);
    const photo = answer ? photoOf(answer, fill.assetId) : undefined;
    if (photo && !fill.manual && collageFacesCut(photo, slot, fill.crop, { faceFraming: r.faceFraming }) > 0) facesCut.push(slot.elementId);
  }
  const active = slots.length;
  const missing = active - filled;
  const spares = answer ? collageSpares(answer, slots.map((s) => s.elementId)).length : 0;
  return {
    active,
    filled,
    missing,
    distinct: distinct.size,
    spares,
    repeats: filled - distinct.size,
    facesCut,
    complete: active > 0 && (r.allowEmpty ? filled > 0 : missing === 0),
  };
}

// ── Capability (get_capabilities.photoCollage) ───────────────────────────────

/** For `get_capabilities`: what a photo collage is, what an agent may set, and the limits. */
export const PHOTO_COLLAGE_CAPABILITY = {
  applies: 'Many shopper photos on flat print sides (Kutije sa slikama: an A3 sheet with 15–24 photos plus quote boxes, or a box printed on two sheets such as 10 + 14). One question takes every photo at once, over every sheet; the storefront places them on the slots itself, and the shopper swaps photos, re-frames any single one and shuffles.',
  question: {
    type: PHOTO_COLLAGE_OPTION_TYPE,
    config: {
      targetElementIds: `ordered slot layer ids, ${COLLAGE_MIN_SLOTS}–${COLLAGE_MAX_SLOTS}, unique; slot n is "Photo n" on the order; may span several layout folders (count variants) — only the slots visible after resolve are active`,
      allowRepeats: 'default true — the shopper may fill empty slots with repeats of their photos',
      allowEmpty: 'default false — every active slot must hold a photo before the order can be placed (422 collage-incomplete)',
      maxSpare: `0–${COLLAGE_MAX_SPARE}, default ${COLLAGE_DEFAULT_SPARE} — extra photos kept aside ("Rezerva"); never printed`,
      faceFraming: 'default true — automatic framing keeps detected faces whole (free local face detector)',
      buttonText: 'optional picker button label; the default is "Izaberi slike"',
    },
  },
  slots: 'Image layers with source upload and fit cover, inside a folder flagged SceneGroup.collage (an authoring hint; the renderer never reads it). Masks and clipShape are fine. Not allowed on a slot: removeBackground, faceCutout, blurBackground, aiStyle, enhance, headFit. Keep shopper move/scale true so each photo can be re-framed.',
  answer: '{v:1, kind:"collage", photos:[{assetId, w?, h?, faces?}], slots:{[elementId]:{assetId, crop?, manual?}}, seed?} — keyed by element id and normalised by normalizeCollageAnswer. Photos in no active slot are spares and never print. Each filled slot prints exactly like a framed single photo (projectCollageValues), so preview = print.',
  placement: 'planCollagePlacement: an optimal assignment. Orientation comes first: a landscape photo goes into a tall slot (or a tall photo into a wide one) only as often as the photos leave no other way, for every seed. Then shape match, faces kept whole, print resolution and photo size against slot size, plus a seeded random term that only decides between equally good options. Seed 0 is deterministic. Shuffling rearranges the printed photos only; spares stay spares.',
  framing: 'collageAutoCrop: zoom 1 (the whole photo at cover fit), the window slid along the one cropped axis so faces stay whole; without faces a tall photo keeps its upper part. Moving a photo to another slot re-frames it automatically; a hand framing stays only while the photo stays in its slot.',
  limits: {
    minSlots: COLLAGE_MIN_SLOTS,
    maxSlots: COLLAGE_MAX_SLOTS,
    defaultSpare: COLLAGE_DEFAULT_SPARE,
    maxSpare: COLLAGE_MAX_SPARE,
    maxFacesPerPhoto: COLLAGE_MAX_FACES,
    maxZoom: COLLAGE_MAX_ZOOM,
    drawCopySide: COLLAGE_DRAW_SIDE,
    thumbnailSide: COLLAGE_THUMB_SIDE,
  },
  rules: [
    'One collage question fills all of its slots; never add per-slot photo fields and never hand-wire slot bindings.',
    'Only the slots visible after resolve count; an order needs every one of them filled unless allowEmpty.',
    'Moving a photo to another slot re-frames it automatically.',
    'Spare photos stay in the answer but never print.',
  ],
  paid: 'No paid service is ever called for a collage slot: faces come from the free local detector, authoring refuses paid effects on slots, the storefront never runs cut-outs or styles on collage photos, and the print worker strips such effects as a last guard.',
  authoring: 'Blueprint collages[] or MCP configure_photo_collage — the same shared service Studio uses writes the question config and exactly one image binding per slot. render_review can render filled-collage scenarios; build their answers with planCollagePlacement and collageAutoCrop and check them with normalizeCollageAnswer.',
  humanOnly: 'Publishing, product connection, pinning and rollback are human actions; agents never publish. Prices per photo count are set by a human in Studio.',
} as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isSeed(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= -0x80000000 && value <= 0x7fffffff;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function round(value: number, places: number): number {
  const f = 10 ** places;
  const r = Math.round(value * f) / f;
  return Object.is(r, -0) ? 0 : r;
}

function sameCrop(a: ImageCrop | undefined, b: ImageCrop | undefined, tolerance = 0): boolean {
  if (!a || !b) return a === b;
  const close = (p: number | undefined, q: number | undefined) => Math.abs((p ?? 0) - (q ?? 0)) <= tolerance + 1e-12;
  return close(a.cx, b.cx) && close(a.cy, b.cy) && close(a.zoom, b.zoom) && close(a.rotation, b.rotation);
}

function sameFill(a: CollageFill, b: CollageFill): boolean {
  return a.assetId === b.assetId && (a.manual === true) === (b.manual === true) && sameCrop(a.crop, b.crop);
}

function lexLess(a: readonly number[], b: readonly number[]): boolean {
  for (let i = 0; i < a.length; i++) {
    if (a[i]! < b[i]! - 1e-12) return true;
    if (a[i]! > b[i]! + 1e-12) return false;
  }
  return false;
}

/** Two slots close enough that the same photo in both reads as a mistake (within 1.6 × their size). */
function nextTo(a: CollageSlotGeom, b: CollageSlotGeom): boolean {
  if (a.sideId !== b.sideId) return false;
  if (a.cx === undefined || a.cy === undefined || b.cx === undefined || b.cy === undefined) return false;
  const reach = 1.6 * (Math.max(a.w, a.h) + Math.max(b.w, b.h)) / 2;
  return Math.hypot(a.cx - b.cx, a.cy - b.cy) < reach;
}
